
import Vue from 'vue';
import Component from 'vue-class-component';
import { BoListPanel } from 'bonomo/dist';
import Datim from '@/components/shared/Datim.vue';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';
import { subscriptionService, ISubscriptionListItem } from '@/services/subscriptionService';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import OrderSectionTabs from '@/components/order/order/OrderSectionTabs.vue';
import {
  PAGINATION_DEFAULT_PER_PAGE,
  PAGINATION_PER_PAGE_OPTIONS,
} from '@/constants/pagination';

const STATUS_COLORS: Record<string, string> = {
  active:             'green darken-1',
  trialing:           'blue',
  past_due:           'orange darken-1',
  canceled:           'red darken-1',
  unpaid:             'red',
  paused:             'grey darken-1',
  incomplete:         'orange',
  incomplete_expired: 'grey',
};

const STATUS_ICONS: Record<string, string> = {
  active:             'mdi-check-decagram',
  trialing:           'mdi-timer-sand',
  past_due:           'mdi-alert-circle',
  canceled:           'mdi-cancel',
  unpaid:             'mdi-credit-card-off',
  paused:             'mdi-pause-circle',
  incomplete:         'mdi-progress-clock',
  incomplete_expired: 'mdi-clock-alert-outline',
};

const ALL_STATUSES = [
  'active',
  'trialing',
  'past_due',
  'canceled',
  'unpaid',
  'paused',
  'incomplete',
  'incomplete_expired',
];

interface ISubscriptionViewItem extends ISubscriptionListItem {
  id: number;
  plan?: any;
  stripe_subscription_id?: string;
  created_at?: string | null;
}

Vue.filter('truncateStripe', (val: string) => {
  if (!val) return '';
  return val.length > 18 ? val.substring(0, 10) + '…' + val.slice(-6) : val;
});

@Component({
  name: 'SubscriptionList',
  components: {
    BoListPanel,
    Datim,
    PaginationBar,
    OrderSectionTabs,
  },
})
export default class SubscriptionList extends Vue {
  private subscriptions: ISubscriptionViewItem[] = [];
  private loading: boolean = false;
  private actionRunningIds: number[] = [];
  private search: string = '';
  private filterStatus: string | null = null;
  private total: number | null = null;
  private currentPage: number = 1;
  private lastPage: number = 1;
  private perPage: number = PAGINATION_DEFAULT_PER_PAGE;
  readonly perPageOptions: number[] = PAGINATION_PER_PAGE_OPTIONS;
  private copiedStripeId: string | null = null;
  private ordersDialog: boolean = false;
  private ordersDialogLoading: boolean = false;
  private ordersDialogOrders: any[] = [];
  private ordersDialogSubscription: ISubscriptionViewItem | null = null;
  private cancelDialog: boolean = false;
  private reactivateDialog: boolean = false;
  private renewDialog: boolean = false;
  private pauseDialog: boolean = false;
  private pauseMode: 'indefinite' | 'until_date' = 'indefinite';
  private pauseResumeDate: string = '';
  private renewPreviewLoading: boolean = false;
  private renewPreview: any = null;
  private refundsHistoryDialog: boolean = false;
  private refundsHistorySubscription: ISubscriptionViewItem | null = null;
  private refundDialog: boolean = false;
  private refundPreviewLoading: boolean = false;
  private refundPreview: any = null;
  private refundPreviewError: string = '';
  private refundType: 'full' | 'partial' = 'full';
  private refundAmount: string = '';
  private refundAction: 'refund_and_cancel' | 'refund_only' = 'refund_only';
  private pendingActionItem: ISubscriptionViewItem | null = null;

  get refundAmountError(): string {
    if (this.refundType !== 'partial' || !this.refundAmount || !this.refundPreview) return '';
    const entered = parseFloat(this.refundAmount);
    if (isNaN(entered) || entered <= 0) return this.$t('subscriptions.list.refundAmountMin') as string;
    if (entered > this.refundPreview.refundable_amount) {
      return `${this.$t('subscriptions.list.refundAmountMax')} ${this.refundPreview.refundable_amount.toFixed(2)} ${(this.refundPreview.currency || 'eur').toUpperCase()}`;
    }
    return '';
  }

  get minPauseDate(): string {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  }

  get hasActiveFilters(): boolean {
    return !!this.filterStatus;
  }

  get statusSummary(): Array<{ value: string, label: string, count: number }> {
    const counts: Record<string, number> = {};
    this.subscriptions.forEach((sub) => {
      const key = sub.status || 'unknown';
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.keys(counts)
      .filter((key) => key !== 'unknown')
      .map((key) => ({
        value: key,
        label: (this.$t('subscriptions.statuses.' + key) as string) || key,
        count: counts[key],
      }))
      .sort((a, b) => b.count - a.count);
  }

  get statusOptions() {
    return ALL_STATUSES.map((s) => ({
      value: s,
      label: this.$t('subscriptions.statuses.' + s) as string || s,
    }));
  }

  get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('common.id') as string,                                              value: 'id',                    align: 'start', sortable: true  },
      { text: this.$t('common.name') as string,                                            value: 'user',                  align: 'start', sortable: false },
      { text: this.$t('common.serviceProvider') as string,                                 value: 'service_provider',      align: 'start', sortable: false },
      { text: this.$t('subscriptions.list.plan') as string,                                value: 'plan',                  align: 'start', sortable: false },
      { text: this.$t('subscriptions.list.amount') as string,                              value: 'amount',                align: 'start', sortable: true  },
      { text: this.$t('common.status') as string,                                          value: 'status',                align: 'start', sortable: true  },
      { text: this.$t('subscriptions.list.stripeSubscriptionId') as string,                value: 'stripe_subscription_id', align: 'start', sortable: false },
      { text: this.$t('subscriptions.list.currentPeriodEnd') as string,                   value: 'current_period_end',    align: 'start', sortable: true  },
      { text: this.$t('subscriptions.list.canceledAt') as string,                          value: 'canceled_at',           align: 'start', sortable: true  },
      { text: this.$t('common.createdAt') as string,                                       value: 'created_at',            align: 'start', sortable: true  },
      { text: '',                                                                            value: 'action',               align: 'center', sortable: false },
    ];
  }

  get dialogTitle(): string {
    if (!this.ordersDialogSubscription) {
      return this.$t('subscriptions.list.ordersModalTitle') as string;
    }
    return `${this.$t('subscriptions.list.ordersModalTitle')} #${this.ordersDialogSubscription.id}`;
  }

  get filteredSubscriptions(): ISubscriptionViewItem[] {
    return this.subscriptions;
  }

  private mounted() {
    this.loadData();
  }

  private async loadData() {
    this.loading = true;
    try {
      const params: Record<string, any> = { page: this.currentPage, per_page: this.perPage };
      if (this.filterStatus) {
        params.status = this.filterStatus;
      }
      const result = await subscriptionService.fetchSubscriptionsRaw(params);
      this.subscriptions = ((result && result.data) || []).filter((item: any) => item && item.id);
      this.total = (result && result.total) != null ? result.total : this.subscriptions.length;
      this.lastPage = (result && result.last_page) || 1;
    } finally {
      this.loading = false;
    }
  }

  private statusColor(status: string): string {
    return STATUS_COLORS[status] || 'grey';
  }

  private statusIcon(status: string): string {
    return STATUS_ICONS[status] || 'mdi-help-circle-outline';
  }

  private setQuickStatus(status: string | null) {
    if (this.filterStatus === status) {
      return;
    }
    this.filterStatus = status;
    this.currentPage = 1;
    this.loadData();
  }

  private clearFilters() {
    this.filterStatus = null;
    this.currentPage = 1;
    this.loadData();
  }

  private onPageChange(page: number) {
    this.currentPage = page;
    this.loadData();
  }

  private onPerPageChange(value: number) {
    this.perPage = value;
    this.currentPage = 1;
    this.loadData();
  }

  private userName(user: any): string {
    if (!user) return '—';
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name;
    return name || `#${user.id}`;
  }

  private userLabel(user: any): string {
    if (!user) return '—';
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name;
    return name || user.email || `#${user.id}`;
  }

  private providerLabel(sp: any): string {
    if (!sp) return '—';
    return sp.name || `#${sp.id}`;
  }

  private currencySymbol(currency?: string | null): string {
    const symbols: Record<string, string> = { eur: '€', gbp: '£' };
    const code = (currency || 'eur').toLowerCase();
    return symbols[code] || `${code.toUpperCase()} `;
  }

  private async openOrdersDialog(subscription: ISubscriptionViewItem) {
    this.ordersDialog = true;
    this.ordersDialogLoading = true;
    this.ordersDialogSubscription = subscription;
    this.ordersDialogOrders = [];
    try {
      this.ordersDialogOrders = await subscriptionService.fetchOrdersBySubscription(subscription.id);
    } finally {
      this.ordersDialogLoading = false;
    }
  }

  private closeOrdersDialog() {
    this.ordersDialog = false;
    this.ordersDialogLoading = false;
    this.ordersDialogOrders = [];
    this.ordersDialogSubscription = null;
  }

  private orderStatusLabel(order: any): string {
    const status = order && order.status ? order.status : null;
    if (!status) {
      return '-';
    }
    const key = 'orders.order.statuses.' + status;
    const translated = this.$t(key) as string;
    return translated === key ? status : translated;
  }

  private humanCancellationReason(reason?: string | null): string {
    if (!reason) {
      return '-';
    }
    return reason.replace(/_/g, ' ');
  }

  private formatOrderAmount(order: any): string {
    const amount = Number(order && (order.grand_total || order.total || 0));
    if (isNaN(amount)) {
      return '-';
    }
    return amount.toFixed(2) + ' EUR';
  }

  private openOrderDetails(orderId: number) {
    this.$router.push({ name: 'order', params: { orderId: '' + orderId } });
  }

  private copyToClipboard(text: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
    this.copiedStripeId = text;
    setTimeout(() => { this.copiedStripeId = null; }, 1500);
  }

  private isActionRunning(subscriptionId: number): boolean {
    return this.actionRunningIds.includes(subscriptionId);
  }

  private setActionRunning(subscriptionId: number, running: boolean) {
    if (running) {
      if (!this.actionRunningIds.includes(subscriptionId)) {
        this.actionRunningIds.push(subscriptionId);
      }
      return;
    }
    this.actionRunningIds = this.actionRunningIds.filter((id) => id !== subscriptionId);
  }

  private canCancel(item: ISubscriptionViewItem): boolean {
    return ['active', 'trialing', 'past_due', 'unpaid'].includes(item.status || '') && !item.cancel_at_period_end;
  }

  private canRetry(item: ISubscriptionViewItem): boolean {
    return ['past_due', 'unpaid', 'incomplete'].includes(item.status || '');
  }

  private canReactivate(item: ISubscriptionViewItem): boolean {
    return item.status === 'active' && item.cancel_at_period_end === true;
  }

  private canRenew(item: ISubscriptionViewItem): boolean {
    return ['canceled', 'incomplete_expired'].includes(item.status || '') && !item.renewed_to_subscription_id;
  }

  private canPause(item: ISubscriptionViewItem): boolean {
    return ['active', 'trialing'].includes(item.status || '') && !item.cancel_at_period_end && !item.pause_collection_behavior;
  }

  private canResumeFromPause(item: ISubscriptionViewItem): boolean {
    return !!item.pause_collection_behavior || item.status === 'paused';
  }

  private canRefund(item: ISubscriptionViewItem): boolean {
    return ['active', 'trialing', 'past_due', 'unpaid', 'canceled', 'paused'].includes(item.status || '');
  }

  private openCancelDialog(item: ISubscriptionViewItem) {
    this.pendingActionItem = item;
    this.cancelDialog = true;
  }

  private closeCancelDialog() {
    if (this.pendingActionItem && this.isActionRunning(this.pendingActionItem.id)) {
      return;
    }
    this.cancelDialog = false;
    this.pendingActionItem = null;
  }

  private async confirmCancel() {
    if (!this.pendingActionItem) return;
    const item = this.pendingActionItem;
    await this.runSubscriptionAction('cancel', item);
    this.cancelDialog = false;
    this.pendingActionItem = null;
  }

  private openReactivateDialog(item: ISubscriptionViewItem) {
    this.pendingActionItem = item;
    this.reactivateDialog = true;
  }

  private closeReactivateDialog() {
    if (this.pendingActionItem && this.isActionRunning(this.pendingActionItem.id)) {
      return;
    }
    this.reactivateDialog = false;
    this.pendingActionItem = null;
  }

  private async confirmReactivate() {
    if (!this.pendingActionItem) return;
    const item = this.pendingActionItem;
    await this.runSubscriptionAction('reactivate', item);
    this.reactivateDialog = false;
    this.pendingActionItem = null;
  }

  private async openRenewDialog(item: ISubscriptionViewItem) {
    this.pendingActionItem = item;
    this.renewPreview = null;
    this.renewDialog = true;
    this.renewPreviewLoading = true;
    try {
      this.renewPreview = await subscriptionService.fetchRenewPreview(item.id);
    } catch (_e) {
      // preview failed, still allow confirm with basic info
    } finally {
      this.renewPreviewLoading = false;
    }
  }

  private closeRenewDialog() {
    if (this.pendingActionItem && this.isActionRunning(this.pendingActionItem.id)) {
      return;
    }
    this.renewDialog = false;
    this.pendingActionItem = null;
    this.renewPreview = null;
  }

  private async confirmRenew() {
    if (!this.pendingActionItem) return;
    const item = this.pendingActionItem;
    await this.runSubscriptionAction('renew', item);
    this.renewDialog = false;
    this.renewPreview = null;
    this.pendingActionItem = null;
  }

  private openPauseDialog(item: ISubscriptionViewItem) {
    this.pendingActionItem = item;
    this.pauseMode = 'indefinite';
    this.pauseResumeDate = '';
    this.pauseDialog = true;
  }

  private closePauseDialog() {
    if (this.pendingActionItem && this.isActionRunning(this.pendingActionItem.id)) {
      return;
    }
    this.pauseDialog = false;
    this.pendingActionItem = null;
    this.pauseResumeDate = '';
  }

  private async confirmPause() {
    if (!this.pendingActionItem) return;
    const item = this.pendingActionItem;
    const resumeAt = this.pauseMode === 'until_date' && this.pauseResumeDate ? this.pauseResumeDate : undefined;
    this.setActionRunning(item.id, true);
    try {
      await subscriptionService.pauseSubscription(item.id, resumeAt);
      this.$uiBus.emit('success', this.$t('subscriptions.list.action_pause_ok'));
      await this.loadData();
    } catch (_error) {
      this.$uiBus.emit('alert', this.$t('common.error'), this.$t('common.errorHappened'));
    } finally {
      this.setActionRunning(item.id, false);
    }
    this.pauseDialog = false;
    this.pendingActionItem = null;
    this.pauseResumeDate = '';
  }

  private openRefundsHistoryDialog(item: ISubscriptionViewItem) {
    this.refundsHistorySubscription = item;
    this.refundsHistoryDialog = true;
  }

  private closeRefundsHistoryDialog() {
    this.refundsHistoryDialog = false;
    this.refundsHistorySubscription = null;
  }

  private async openRefundDialog(item: ISubscriptionViewItem) {
    this.pendingActionItem = item;
    this.refundType = 'full';
    this.refundAmount = '';
    this.refundAction = 'refund_only';
    this.refundPreview = null;
    this.refundPreviewError = '';
    this.refundDialog = true;
    this.refundPreviewLoading = true;
    try {
      this.refundPreview = await subscriptionService.fetchRefundPreview(item.id);
    } catch (_e) {
      this.refundPreviewError = (_e && (_e as any).response && (_e as any).response.data && (_e as any).response.data.message) || this.$t('common.errorHappened') as string;
    } finally {
      this.refundPreviewLoading = false;
    }
  }

  private closeRefundDialog() {
    if (this.pendingActionItem && this.isActionRunning(this.pendingActionItem.id)) {
      return;
    }
    this.refundDialog = false;
    this.pendingActionItem = null;
    this.refundPreview = null;
    this.refundPreviewError = '';
    this.refundAmount = '';
  }

  private async confirmRefund() {
    if (!this.pendingActionItem) return;
    const item = this.pendingActionItem;
    const params: { type: 'full' | 'partial'; amount?: number; cancel_after_refund?: boolean } = {
      type: this.refundType,
      cancel_after_refund: this.refundAction === 'refund_and_cancel',
    };
    if (this.refundType === 'partial' && this.refundAmount) {
      params.amount = parseFloat(this.refundAmount);
    }
    this.setActionRunning(item.id, true);
    try {
      await subscriptionService.refundSubscription(item.id, params);
      this.$uiBus.emit('success', this.$t('subscriptions.list.action_refund_ok'));
      await this.loadData();
    } catch (error) {
      const errData = error && error.response && error.response.data && error.response.data.errors && error.response.data.errors[0];
      const msg = (errData && errData.title) || this.$t('common.errorHappened');
      this.$uiBus.emit('alert', this.$t('common.error'), msg);
    } finally {
      this.setActionRunning(item.id, false);
    }
    this.refundDialog = false;
    this.pendingActionItem = null;
    this.refundPreview = null;
    this.refundAmount = '';
  }

  private async runSubscriptionAction(action: 'cancel' | 'retry' | 'reactivate' | 'renew' | 'resumeFromPause', item: ISubscriptionViewItem) {
    if (!item || !item.id || this.isActionRunning(item.id)) {
      return;
    }

    this.setActionRunning(item.id, true);
    try {
      if (action === 'cancel') {
        await subscriptionService.cancelSubscription(item.id);
        this.$uiBus.emit('success', this.$t('subscriptions.list.action_cancel_ok'));
      }
      if (action === 'retry') {
        await subscriptionService.retrySubscription(item.id);
        this.$uiBus.emit('success', this.$t('subscriptions.list.action_retry_ok'));
      }
      if (action === 'reactivate') {
        await subscriptionService.reactivateSubscription(item.id);
        this.$uiBus.emit('success', this.$t('subscriptions.list.action_reactivate_ok'));
      }
      if (action === 'renew') {
        await subscriptionService.renewSubscription(item.id);
        this.$uiBus.emit('success', this.$t('subscriptions.list.action_renew_ok'));
      }
      if (action === 'resumeFromPause') {
        await subscriptionService.resumeFromPauseSubscription(item.id);
        this.$uiBus.emit('success', this.$t('subscriptions.list.action_resume_pause_ok'));
      }
      await this.loadData();
    } catch (_error) {
      this.$uiBus.emit('alert', this.$t('common.error'), this.$t('common.errorHappened'));
    } finally {
      this.setActionRunning(item.id, false);
    }
  }
}
